import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ScrollText, Users, Sparkles, AlertTriangle, Info, ShieldAlert, CreditCard, RefreshCw } from "lucide-react";
import AdminShell from "../../components/AdminShell";
import api from "../../lib/api";
import { LoadingState, ErrorState } from "../../components/LoadingState";
import { ActionButton, CustomerPanel, MetricCard, StatusBadge } from "../../components/customer/CustomerUI";

const METRICS = [
  { key: "active_users", label: "Active Users" },
  { key: "connected_crm_accounts", label: "Connected CRMs" },
  { key: "active_scans", label: "Active Scans" },
  { key: "completed_scans", label: "Completed Scans" },
  { key: "failed_scans", label: "Failed Scans" },
  { key: "pending_approvals", label: "Pending Approvals" },
];

const ALERT_ICON = { critical: ShieldAlert, warning: AlertTriangle, info: Info };
const ALERT_TONE = { critical: "danger", warning: "warning", info: "info" };

export default function CommandCenter() {
  const [data, setData] = useState(null);
  const [chain, setChain] = useState(null);
  const [billingCheck, setBillingCheck] = useState(null);
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => setError(true));
    api.get("/admin/audit/verify").then((r) => setChain(r.data)).catch(() => {});
  }, []);

  const m = data?.metrics || {};

  const reconcileBilling = async (apply = false) => {
    if (apply && !window.confirm("Downgrade every stale Stripe-account subscription to Free? This action is audited.")) return;
    setBillingBusy(true);
    setBillingError("");
    try {
      const { data: result } = await api.post("/admin/billing/stripe/reconcile-account", { apply });
      setBillingCheck(result);
    } catch (e) {
      setBillingError(e.response?.data?.detail || "Billing integrity check failed.");
    } finally {
      setBillingBusy(false);
    }
  };

  if (!data && !error) return <AdminShell title="Command Center" subtitle="Real-time operational overview of the CleanIQ platform"><LoadingState cards={4} rows={4} label="Loading Command Center" /></AdminShell>;
  if (error) return <AdminShell title="Command Center" subtitle="Real-time operational overview of the CleanIQ platform"><ErrorState message="Command Center data is temporarily unavailable." onRetry={() => window.location.reload()} /></AdminShell>;

  return (
    <AdminShell title="Command Center" subtitle="Real-time operational overview of the CleanIQ platform">
      {/* Metric grid */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:gap-4">
        {METRICS.map((met) => (
          <MetricCard
            key={met.key}
            data-testid={`metric-${met.key}`}
            label={met.label}
            value={data ? (m[met.key] ?? 0) : "—"}
            valueClassName="text-3xl tracking-tight lg:text-4xl"
            labelClassName="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500"
          />
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Alerts */}
        <div className="space-y-6 lg:col-span-2">
          <CustomerPanel title="System Alerts">
          <div className="space-y-2 px-ui-4 py-ui-4 sm:px-ui-5">
            {(data?.alerts || []).map((a, i) => {
              const Icon = ALERT_ICON[a.severity] || Info;
              return (
                <div key={i} data-testid="system-alert" className="flex items-center gap-3 rounded-customer border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <StatusBadge tone={ALERT_TONE[a.severity]}><Icon className="h-3.5 w-3.5 shrink-0" /> {a.severity}</StatusBadge>
                  {a.message}
                </div>
              );
            })}
          </div>
          </CustomerPanel>

          <CustomerPanel title="Jump to Center">
          <div className="grid grid-cols-2 gap-3 px-ui-4 py-ui-4 sm:grid-cols-4 sm:px-ui-5">
            {[
              { to: "/admin/security", icon: ShieldCheck, label: "Security" },
              { to: "/admin/audit", icon: ScrollText, label: "Audit" },
              { to: "/admin/access", icon: Users, label: "Access" },
              { to: "/admin/intelligence", icon: Sparkles, label: "Intelligence (Dormant)" },
            ].map((c) => (
              <Link key={c.to} to={c.to} className="flex flex-col items-center gap-2 rounded-customer border border-slate-200 bg-white p-5 text-center text-xs font-semibold text-slate-700 shadow-customer transition hover:-translate-y-0.5 hover:border-blue-500/50 hover:bg-blue-50 hover:text-slate-950">
                <c.icon className="h-5 w-5" /> {c.label}
                {c.to === "/admin/intelligence" ? <StatusBadge tone="neutral">Dormant</StatusBadge> : null}
              </Link>
            ))}
          </div>
          </CustomerPanel>
        </div>

        {/* Status column */}
        <div className="space-y-4">
          <CustomerPanel>
            <div className="px-ui-4 py-ui-4 sm:px-ui-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Platform Status</div>
            <StatusBadge className="mt-2" tone="success">{data?.platform_status || "…"}</StatusBadge>
            </div>
          </CustomerPanel>
          <CustomerPanel data-testid="audit-integrity">
            <div className="px-ui-4 py-ui-4 sm:px-ui-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Audit Chain Integrity</div>
            {chain ? (
              <StatusBadge className="mt-2" tone={chain.intact ? "success" : "danger"}><ShieldCheck className="h-3.5 w-3.5" /> {chain.intact ? "Verified" : "Tamper detected"}</StatusBadge>
            ) : <div className="mt-2 text-slate-500">Checking…</div>}
            <div className="mt-1 font-mono text-[11px] text-slate-500">{chain ? `${chain.checked} events hash-chained` : ""}</div>
            </div>
          </CustomerPanel>
          <CustomerPanel data-testid="billing-integrity">
            <div className="px-ui-4 py-ui-4 sm:px-ui-5">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              <CreditCard className="h-4 w-4" /> Billing Integrity
            </div>
            <div className="mt-2 text-sm text-slate-700">
              {billingCheck
                ? `${billingCheck.stale} stale of ${billingCheck.checked} active Stripe records`
                : "Check paid access against the configured Stripe account."}
            </div>
            {billingCheck && <StatusBadge className="mt-2" tone={billingCheck.stale > 0 ? "warning" : "success"}>{billingCheck.stale > 0 ? `${billingCheck.stale} stale` : "No stale access"}</StatusBadge>}
            {billingCheck?.updated > 0 && <div className="mt-2 text-sm font-semibold text-emerald-700">{billingCheck.updated} account{billingCheck.updated === 1 ? "" : "s"} downgraded to Free.</div>}
            {billingError && <div className="mt-2 text-sm text-red-700">{billingError}</div>}
            <div className="mt-4 flex flex-wrap gap-2">
              <ActionButton variant="secondary" onClick={() => reconcileBilling(false)} disabled={billingBusy}>
                <RefreshCw className={`h-3.5 w-3.5 ${billingBusy ? "animate-spin" : ""}`} /> Check
              </ActionButton>
              {billingCheck?.stale > 0 && <ActionButton variant="destructive" onClick={() => reconcileBilling(true)} disabled={billingBusy}>
                Reconcile stale access
              </ActionButton>}
            </div>
            </div>
          </CustomerPanel>
        </div>
      </div>
    </AdminShell>
  );
}
